/**
 * Renewals board mock data — Story R3 (Renewals Board UI at /renewals).
 *
 * Shaped EXACTLY like RenewalBoardResponseDto so wiring the real GET /renewals/board
 * is a drop-in swap: replace `const board = MOCK_RENEWALS_BOARD` with
 *   `const { data: board } = useQuery(queryKeys.renewalsBoard(), () => apiClient.get('/renewals/board'))`
 * No view-model reshaping required. kundeId is additive + optional on RenewalBoardItemVM.
 *
 * Visual data ported from v0 renewals-view (same company names / POL numbers / Sparten / Prämien)
 * but reshaped to the R2 wire contract: praemie=string, bucket/status/days_left explicit,
 * renewal_date/act_by_date as ISO 'YYYY-MM-DD', kundeId reuses entity-detail/mock.ts k-* ids.
 *
 * Colour token mapping (single source of truth — see also STATUS_META):
 *   orange / informieren → sem-decide  (K4 Entscheid/Achtung amber)
 *   red    / mahnen      → sem-alert   (K4 alert red)
 * There is intentionally no separate "orange" token. sem-decide IS the K4 orange.
 *
 * [Source: docs/concept/2026-06-26_post-stefan-roadmap-and-module-specs.md §41/§49 (R3)]
 */

import type { RenewalBoardResponseDto, RenewalBucket } from '@brokly/shared';
import { RENEWAL_BUCKETS } from '@brokly/shared';
import type { RenewalBoardItemVM } from './types';
import type { BadgeVariant } from '../../components/ui/Badge';
import type { StatusTone } from '../../components/ui/StatusDot';

// ── Bucket display meta ─────────────────────────────────────────────────────
// Neutral chrome only — no urgency colour on the column header.

export const BUCKET_META: Record<RenewalBucket, { label: string }> = {
  overdue: { label: 'Überfällig' },
  le_14d: { label: '≤ 14 Tage' },
  le_30d: { label: '≤ 30 Tage' },
  le_3mo: { label: '≤ 3 Monate' },
  le_6mo: { label: '≤ 6 Monate' },
  gt_6mo: { label: '> 6 Monate' },
  unknown: { label: 'Unbekannt' },
};

// ── Status display meta ─────────────────────────────────────────────────────
// Colour discipline: ONLY urgency status carries sem-* colour.
//   informieren → sem-decide (orange)
//   mahnen      → sem-alert  (red)
//   none        → null (no badge rendered)

export type StatusMeta = { label: string; variant: BadgeVariant; tone: StatusTone } | null;

export const STATUS_META: Record<string, StatusMeta> = {
  // orange = warning: the K4 Achtung/Entscheid amber token (design.md §2 escalation)
  informieren: { label: 'Informieren', variant: 'decide', tone: 'warning' },
  // red = danger: the K4 alert red token
  mahnen: { label: 'Mahnen', variant: 'alert', tone: 'danger' },
  // none: no status — policy outside both windows, chrome stays neutral
  none: null,
};

// ── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Group board items by bucket in RENEWAL_BUCKETS order.
 * Zero-init in canonical order, then push items — matches the backend zeroBucketCounts pattern.
 */
export function groupByBucket(items: RenewalBoardItemVM[]): Record<RenewalBucket, RenewalBoardItemVM[]> {
  const acc: Record<RenewalBucket, RenewalBoardItemVM[]> = {
    overdue: [],
    le_14d: [],
    le_30d: [],
    le_3mo: [],
    le_6mo: [],
    gt_6mo: [],
    unknown: [],
  };
  for (const item of items) {
    acc[item.bucket].push(item);
  }
  return acc;
}

/**
 * Sort items by days_left ascending (most urgent first), nulls last.
 * Pure — returns a new array. Mirrors backend getHeute redList sort.
 */
export function sortByUrgency(items: RenewalBoardItemVM[]): RenewalBoardItemVM[] {
  return [...items].sort((a, b) => {
    if (a.days_left === null && b.days_left === null) return 0;
    if (a.days_left === null) return 1;
    if (b.days_left === null) return -1;
    return a.days_left - b.days_left;
  });
}

// ── Mock data ───────────────────────────────────────────────────────────────
// ≥ 8 items covering all 7 buckets + all 3 statuses.
// Policy numbers / companies / Sparten / Prämien ported from v0 renewals-view.
// kundeId reuses k-* IDs from entity-detail/mock.ts so EntityLink nav lands on real screens.

const MOCK_ITEMS: RenewalBoardItemVM[] = [
  // overdue — mahnen (red)
  {
    policy_id: 'p-r1',
    policy_number: 'POL-339-2266',
    kunde: 'Basler Chemie AG',
    kundeId: 'k-basler',
    versicherer: 'CSS',
    sparte: 'Betriebshaftpflicht',
    praemie: '48200.00',
    currency: 'CHF',
    renewal_date: '2026-06-20',
    act_by_date: '2026-06-22',
    days_left: -4,
    bucket: 'overdue',
    status: 'mahnen',
  },
  // le_14d — mahnen (red)
  {
    policy_id: 'p-r2',
    policy_number: 'POL-118-7740',
    kunde: 'Genève Maritime SA',
    kundeId: 'k-geneve',
    versicherer: 'Swiss Life',
    sparte: 'Transportversicherung',
    praemie: '73900.00',
    currency: 'CHF',
    renewal_date: '2026-07-10',
    act_by_date: '2026-07-03',
    days_left: 7,
    bucket: 'le_14d',
    status: 'mahnen',
  },
  // le_14d — informieren (orange)
  {
    policy_id: 'p-r3',
    policy_number: 'POL-551-9023',
    kunde: 'Alpina Pharma AG',
    kundeId: 'k-alpina',
    versicherer: 'Helvetia',
    sparte: 'Sachversicherung',
    praemie: '31500.00',
    currency: 'CHF',
    renewal_date: '2026-07-12',
    act_by_date: '2026-07-05',
    days_left: 9,
    bucket: 'le_14d',
    status: 'informieren',
  },
  // le_30d — informieren (orange)
  {
    policy_id: 'p-r4',
    policy_number: 'POL-883-4471',
    kunde: 'Bergland Logistik AG',
    kundeId: 'k-bergland',
    versicherer: 'AXA',
    sparte: 'Flottenversicherung',
    praemie: '96400.00',
    currency: 'CHF',
    renewal_date: '2026-07-25',
    act_by_date: '2026-07-17',
    days_left: 21,
    bucket: 'le_30d',
    status: 'informieren',
  },
  // le_3mo — none (neutral)
  {
    policy_id: 'p-r5',
    policy_number: 'POL-771-3340',
    kunde: 'Alpina Pharma AG',
    kundeId: 'k-alpina',
    versicherer: 'Helvetia',
    sparte: 'Cyber',
    praemie: '12900.00',
    currency: 'CHF',
    renewal_date: '2026-09-15',
    act_by_date: '2026-08-27',
    days_left: 62,
    bucket: 'le_3mo',
    status: 'none',
  },
  // le_3mo — none (neutral)
  {
    policy_id: 'p-r6',
    policy_number: 'POL-410-6677',
    kunde: 'Zürcher Präzision AG',
    kundeId: 'k-zpaeg',
    versicherer: 'AXA',
    sparte: 'Maschinenversicherung',
    praemie: '37400.00',
    currency: 'CHF',
    renewal_date: '2026-09-28',
    act_by_date: '2026-09-10',
    days_left: 76,
    bucket: 'le_3mo',
    status: 'none',
  },
  // le_6mo — none (neutral)
  {
    policy_id: 'p-r7',
    policy_number: 'POL-771-5521',
    kunde: 'Basler Chemie AG',
    kundeId: 'k-basler',
    versicherer: 'Mobiliar',
    sparte: 'Betriebsunterbruch',
    praemie: '54300.00',
    currency: 'CHF',
    renewal_date: '2026-10-30',
    act_by_date: '2026-10-01',
    days_left: 97,
    bucket: 'le_6mo',
    status: 'none',
  },
  // gt_6mo — none (neutral)
  {
    policy_id: 'p-r8',
    policy_number: 'POL-145-8830',
    kunde: 'Genève Maritime SA',
    kundeId: 'k-geneve',
    versicherer: 'Helvetia',
    sparte: 'Gebäudeversicherung',
    praemie: '22600.00',
    currency: 'CHF',
    renewal_date: '2027-02-10',
    act_by_date: '2027-01-12',
    days_left: 200,
    bucket: 'gt_6mo',
    status: 'none',
  },
  // unknown — none (no dates / no status)
  {
    policy_id: 'p-r9',
    policy_number: 'POL-204-1185',
    kunde: 'Zürcher Präzision AG',
    kundeId: 'k-zpaeg',
    versicherer: 'Zurich',
    sparte: 'Cyber',
    praemie: '18750.00',
    currency: 'CHF',
    renewal_date: null,
    act_by_date: null,
    days_left: null,
    bucket: 'unknown',
    status: 'none',
  },
];

// Derive bucketCounts from the items array (single source of truth)
function computeBucketCounts(items: RenewalBoardItemVM[]): Record<RenewalBucket, number> {
  const counts = Object.fromEntries(RENEWAL_BUCKETS.map((b) => [b, 0])) as Record<RenewalBucket, number>;
  for (const item of items) {
    counts[item.bucket]++;
  }
  return counts;
}

export const MOCK_RENEWALS_BOARD: RenewalBoardResponseDto & { items: RenewalBoardItemVM[] } = {
  items: MOCK_ITEMS,
  bucketCounts: computeBucketCounts(MOCK_ITEMS),
};
